import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Database,
  BarChart3,
  Clock,
  Play,
  Settings2,
  Loader2,
  Server,
  HardDrive,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const STATUS_CONFIG = {
  connected: {
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    icon: CheckCircle,
    label: 'Connected',
  },
  local: {
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    icon: HardDrive,
    label: 'Local',
  },
  error: {
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    icon: XCircle,
    label: 'Error',
  },
  unknown: {
    color: 'text-zinc-400',
    bg: 'bg-zinc-500/10',
    border: 'border-zinc-500/30',
    icon: AlertTriangle,
    label: 'Unknown',
  },
  not_initialized: {
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    icon: AlertTriangle,
    label: 'Not Initialized',
  },
};

export default function GCPInfrastructurePanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState({ cloudsql: false, bigquery: false });
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [newInterval, setNewInterval] = useState(15);
  const [savingConfig, setSavingConfig] = useState(false);
  const [triggeringRefresh, setTriggeringRefresh] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setError(null);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gcp/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('eva_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        if (data.bigquery?.scheduler) {
          setNewInterval(data.bigquery.scheduler.refresh_interval_minutes || 15);
        }
      } else {
        setError(`Failed to load status (HTTP ${response.status})`);
      }
    } catch (err) {
      console.error('Failed to load GCP status:', err);
      setError('Unable to connect to backend');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadStatus();
  };

  const handleTestConnection = async (service) => {
    setTesting({ ...testing, [service]: true });
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gcp/${service}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('eva_token')}`,
        },
      });
      const result = await response.json();
      
      if (result.success) {
        toast.success(`${service === 'cloudsql' ? 'Cloud SQL' : 'BigQuery'} connection successful`, {
          description: result.message,
        });
      } else {
        toast.error(`${service === 'cloudsql' ? 'Cloud SQL' : 'BigQuery'} connection failed`, {
          description: result.message,
        });
      }
      loadStatus();
    } catch (error) {
      toast.error(`Connection test failed`, {
        description: error.message,
      });
    } finally {
      setTesting({ ...testing, [service]: false });
    }
  };

  const handleToggleScheduler = async (start) => {
    try {
      const action = start ? 'start' : 'stop';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gcp/bigquery/scheduler/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('eva_token')}`,
        },
      });
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Scheduler ${start ? 'started' : 'stopped'}`);
      } else {
        toast.error(`Failed to ${action} scheduler`, { description: result.message });
      }
      loadStatus();
    } catch {
      toast.error('Failed to toggle scheduler');
    }
  };

  const handleSaveInterval = async () => {
    setSavingConfig(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/gcp/bigquery/scheduler/configure?interval_minutes=${newInterval}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('eva_token')}`,
          },
        }
      );
      const result = await response.json();
      
      if (result.success) {
        toast.success('Refresh interval updated', {
          description: `Mart tables will refresh every ${newInterval} minutes`,
        });
        setConfigDialogOpen(false);
        loadStatus();
      } else {
        toast.error('Failed to update interval');
      }
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTriggerRefresh = async (fullRefresh = false) => {
    setTriggeringRefresh(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/gcp/bigquery/scheduler/trigger?full_refresh=${fullRefresh}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('eva_token')}`,
          },
        }
      );
      const result = await response.json();
      
      if (result.success) {
        toast.success('Mart tables refreshed', {
          description: `Status: ${result.result?.status}`,
        });
        loadStatus();
      } else {
        toast.error('Refresh failed', { description: result.message });
      }
    } catch {
      toast.error('Failed to trigger refresh');
    } finally {
      setTriggeringRefresh(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'Never';
    const date = new Date(ts);
    return date.toLocaleString();
  };

  const formatTimeAgo = (ts) => {
    if (!ts) return 'Never';
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  if (loading) {
    return (
      <Card data-testid="gcp-infrastructure-panel">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !status) {
    return (
      <Card data-testid="gcp-infrastructure-panel">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5 text-muted-foreground" />
              Database Infrastructure
            </CardTitle>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Retry
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-red-500">
            <XCircle className="h-4 w-4" />
            <p className="text-sm">{error || 'Unable to load infrastructure status'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const cloudsql = status.cloudsql || {};
  const bigquery = status.bigquery || {};
  const scheduler = bigquery.scheduler || {};

  const CloudSQLConfig = STATUS_CONFIG[cloudsql.status] || STATUS_CONFIG.unknown;
  const BigQueryConfig = STATUS_CONFIG[bigquery.status] || STATUS_CONFIG.unknown;
  const CloudSQLIcon = CloudSQLConfig.icon;
  const BigQueryIcon = BigQueryConfig.icon;

  return (
    <Card data-testid="gcp-infrastructure-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              Database Infrastructure
            </CardTitle>
            <CardDescription className="mt-1">
              Application and analytics database connectivity
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-gcp-status-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Application Database - Cloud SQL / SQLite */}
        <div 
          className={`p-4 rounded-lg border ${CloudSQLConfig.border} ${CloudSQLConfig.bg}`}
          data-testid="cloudsql-status"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-background/80">
                <Database className={`h-5 w-5 ${CloudSQLConfig.color}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Application Database</span>
                  <Badge variant="secondary" className={`text-xs ${CloudSQLConfig.color}`}>
                    {CloudSQLConfig.label}
                  </Badge>
                  <Badge variant="outline" className="text-xs font-mono">
                    {cloudsql.backend === 'postgresql' ? 'PostgreSQL' : 'SQLite'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {cloudsql.backend === 'postgresql' 
                    ? 'Cloud SQL PostgreSQL - production database' 
                    : 'SQLite - local development database'}
                </p>
                {cloudsql.stats?.version && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {cloudsql.stats.version} • Size: {cloudsql.stats.size || 'N/A'}
                  </p>
                )}
                {cloudsql.config?.host && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Host: {cloudsql.config.host}:{cloudsql.config.port}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTestConnection('cloudsql')}
                disabled={testing.cloudsql}
                data-testid="test-cloudsql-btn"
              >
                {testing.cloudsql ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <CloudSQLIcon className={`h-5 w-5 ${CloudSQLConfig.color}`} />
            </div>
          </div>
          
          {/* Application DB Metrics */}
          {cloudsql.metrics && Object.keys(cloudsql.metrics).length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Max Latency</span>
                  <span className="font-medium text-foreground">
                    {cloudsql.metrics.max_latency_ms !== undefined
                      ? `${cloudsql.metrics.max_latency_ms}ms`
                      : 'N/A'}
                  </span>
                </div>
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Error Rate</span>
                  <span className="font-medium text-foreground">
                    {cloudsql.metrics.error_rate || '0%'}
                  </span>
                </div>
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Data Freshness</span>
                  <span className="font-medium text-foreground">
                    {cloudsql.metrics.data_freshness_mins !== undefined 
                      ? (cloudsql.metrics.data_freshness_mins < 1 
                          ? 'Just now' 
                          : `${Math.round(cloudsql.metrics.data_freshness_mins)}m ago`)
                      : 'N/A'}
                  </span>
                </div>
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Storage</span>
                  <span className="font-medium text-foreground">
                    {cloudsql.stats?.size || 'N/A'}
                  </span>
                </div>
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Ops (24h)</span>
                  <span className="font-medium text-foreground">
                    {cloudsql.metrics.throughput_24h !== undefined 
                      ? cloudsql.metrics.throughput_24h 
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Analytics Database - BigQuery / DuckDB */}
        <div 
          className={`p-4 rounded-lg border ${BigQueryConfig.border} ${BigQueryConfig.bg}`}
          data-testid="bigquery-status"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-background/80">
                <BarChart3 className={`h-5 w-5 ${BigQueryConfig.color}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Analytics Database</span>
                  <Badge variant="secondary" className={`text-xs ${BigQueryConfig.color}`}>
                    {BigQueryConfig.label}
                  </Badge>
                  <Badge variant="outline" className="text-xs font-mono">
                    {bigquery.backend === 'bigquery' ? 'BigQuery' : 'DuckDB'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {bigquery.backend === 'bigquery'
                    ? 'Google BigQuery - cloud data warehouse'
                    : 'DuckDB - local analytics engine'}
                </p>
                {bigquery.config?.project_id && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Project: {bigquery.config.project_id} / Dataset: {bigquery.config.dataset_id}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Tables: {bigquery.tables_ready || 0}/{bigquery.tables_total || 0} ready
                  {bigquery.stats?.total_rows > 0 && ` • ${bigquery.stats.total_rows.toLocaleString()} rows`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTestConnection('bigquery')}
                disabled={testing.bigquery}
                data-testid="test-bigquery-btn"
              >
                {testing.bigquery ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <BigQueryIcon className={`h-5 w-5 ${BigQueryConfig.color}`} />
            </div>
          </div>

          {/* Analytics DB Metrics */}
          {bigquery.metrics && Object.keys(bigquery.metrics).length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Max Latency</span>
                  <span className="font-medium text-foreground">
                    {bigquery.metrics.max_latency_ms !== undefined
                      ? `${bigquery.metrics.max_latency_ms}ms`
                      : 'N/A'}
                  </span>
                </div>
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Error Rate</span>
                  <span className="font-medium text-foreground">
                    {bigquery.metrics.error_rate || '0%'}
                  </span>
                </div>
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Data Freshness</span>
                  <span className="font-medium text-foreground">
                    {bigquery.metrics.data_freshness === 'N/A (run analytics refresh to populate)'
                      ? 'Needs refresh'
                      : bigquery.metrics.data_freshness || 'N/A'}
                  </span>
                </div>
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Storage</span>
                  <span className="font-medium text-foreground">
                    {bigquery.metrics.storage || 'N/A'}
                  </span>
                </div>
                <div className="bg-background/50 rounded p-2">
                  <span className="block text-muted-foreground">Total Rows</span>
                  <span className="font-medium text-foreground">
                    {bigquery.stats?.total_rows !== undefined 
                      ? bigquery.stats.total_rows.toLocaleString() 
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Table Status Breakdown */}
          {(bigquery.backend === 'duckdb' || bigquery.backend === 'bigquery') && bigquery.tables && Object.keys(bigquery.tables).length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Table Status</span>
                <span className="text-xs text-muted-foreground">
                  {bigquery.tables_ready || 0} ready / {bigquery.tables_total || 0} total
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {Object.entries(bigquery.tables).map(([tableName, tableInfo]) => (
                  <div
                    key={tableName}
                    className={`relative p-2 rounded ${
                      tableInfo.status === 'ready'
                        ? 'bg-emerald-500/10 border border-emerald-500/20'
                        : tableInfo.status === 'empty'
                        ? 'bg-amber-500/10 border border-amber-500/20'
                        : 'bg-red-500/10 border border-red-500/20'
                    }`}
                  >
                    {tableInfo.stale && (
                      <span
                        className="absolute top-1 right-1 text-[9px] font-medium px-1 rounded bg-orange-500/15 text-orange-600 border border-orange-500/30"
                        title={tableInfo.last_modified ? `Last modified: ${tableInfo.last_modified}` : 'Freshness unknown'}
                        data-testid={`mart-stale-badge-${tableName}`}
                      >
                        Stale
                      </span>
                    )}
                    <span className="block font-mono text-foreground truncate" title={tableName}>
                      {tableName.replace('mart_', '')}
                    </span>
                    <span className={`text-[10px] ${
                      tableInfo.status === 'ready'
                        ? 'text-emerald-600'
                        : tableInfo.status === 'empty'
                        ? 'text-amber-600'
                        : 'text-red-600'
                    }`}>
                      {tableInfo.status === 'ready'
                        ? `${tableInfo.row_count?.toLocaleString() || 0} rows`
                        : tableInfo.status === 'empty'
                        ? 'Empty'
                        : 'Missing'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analytics Refresh Scheduler */}
          {bigquery.backend === 'bigquery' && (
            <div className="mt-4 pt-4 border-t border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Analytics Refresh Scheduler</span>
                  <Badge 
                    variant={scheduler.running ? 'default' : 'secondary'}
                    className={scheduler.running ? 'bg-emerald-500/10 text-emerald-600' : ''}
                  >
                    {scheduler.running ? 'Running' : 'Stopped'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfigDialogOpen(true)}
                    data-testid="configure-scheduler-btn"
                  >
                    <Settings2 className="h-4 w-4" />
                  </Button>
                  <Switch
                    checked={scheduler.running}
                    onCheckedChange={(checked) => handleToggleScheduler(checked)}
                    data-testid="scheduler-toggle"
                  />
                </div>
              </div>
              
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                <div>
                  <span className="block text-foreground font-medium">{scheduler.refresh_interval_minutes || 15}m</span>
                  Refresh interval
                </div>
                <div>
                  <span className="block text-foreground font-medium">{scheduler.total_refreshes || 0}</span>
                  Total refreshes
                </div>
                <div>
                  <span className="block text-foreground font-medium">{formatTimeAgo(scheduler.last_refresh_at)}</span>
                  Last refresh
                </div>
                <div>
                  <span className="block text-foreground font-medium">{scheduler.last_refresh_status || 'N/A'}</span>
                  Last status
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTriggerRefresh(false)}
                  disabled={triggeringRefresh}
                  data-testid="trigger-refresh-btn"
                >
                  {triggeringRefresh ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-1.5" />
                  )}
                  Refresh Now
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTriggerRefresh(true)}
                  disabled={triggeringRefresh}
                  data-testid="full-refresh-btn"
                >
                  Full Refresh
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>

      {/* Configure Scheduler Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              Configure Analytics Refresh
            </DialogTitle>
            <DialogDescription>
              Set how often BigQuery mart tables are refreshed with data from PostgreSQL.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="refresh-interval">Refresh Interval (minutes)</Label>
              <Input
                id="refresh-interval"
                type="number"
                min={1}
                max={1440}
                value={newInterval}
                onChange={(e) => setNewInterval(parseInt(e.target.value) || 15)}
                data-testid="refresh-interval-input"
              />
              <p className="text-xs text-muted-foreground">
                Minimum: 1 minute, Maximum: 1440 minutes (24 hours).
                Recommended: 15-60 minutes for most use cases.
              </p>
            </div>

            <div className="p-3 bg-secondary/50 rounded-lg">
              <p className="text-sm">
                <strong>Current:</strong> Every {scheduler.refresh_interval_minutes || 15} minutes
              </p>
              {scheduler.next_refresh_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Next refresh: {formatTimestamp(scheduler.next_refresh_at)}
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveInterval} 
              disabled={savingConfig}
              data-testid="save-interval-btn"
            >
              {savingConfig ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
